import { BaseService } from './base/BaseService';
import { Worker, WorkerCreate, WorkerUpdate } from '../types/entities';
import { API_ENDPOINTS } from '../constants/api';
import { QueryParams } from '../types/common';

// Re-export Worker type for backward compatibility
export type { Worker, WorkerCreate, WorkerUpdate } from '../types/entities';

export interface WorkerFilters {
  region_id?: number;
  settlement_id?: number;
  available_only?: boolean;
}

export interface AddressFixStats {
  total_workers: number;
  workers_with_settlement: number;
  workers_without_settlement: number;
  percentage_fixed: number;
  examples_needing_fix: Array<{ id: string; address: string }>;
}

export interface AddressFixResult {
  message: string;
  processed: number;
  fixed: number;
  errors: string[];
  total_errors: number;
  next_start_id?: string;
}

/**
 * Service for worker-related API operations
 */
export class WorkerService extends BaseService<Worker, WorkerCreate, WorkerUpdate> {
  constructor() {
    super(API_ENDPOINTS.WORKERS);
  }

  /**
   * List workers with filters
   */
  async list(params?: QueryParams & { filters?: WorkerFilters }): Promise<Worker[]> {
    return super.list(params);
  }

  /**
   * Get transport types
   */
  async getTransportTypes(): Promise<string[]> {
    return this.getRequest(`${this.endpoint}/transport-types`);
  }

  /**
   * Get address fix statistics
   */
  async getAddressFixStats(): Promise<AddressFixStats> {
    return this.getRequest(`${this.endpoint}/address-fix-stats`);
  }

  /**
   * Fix worker addresses in batch
   */
  async fixAddressesBatch(batchSize: number = 50, startId: number = 0): Promise<AddressFixResult> {
    return this.postRequest(`${this.endpoint}/fix-addresses-batch`, null, {
      params: { batch_size: batchSize, start_id: startId }
    });
  }

  /**
   * Toggle worker availability
   */
  async toggleAvailability(id: number): Promise<Worker> {
    return this.putRequest(`${this.endpoint}/${id}/toggle-availability`);
  }
}

// Create singleton instance
export const workerService = new WorkerService();

// Export convenience functions for backward compatibility
export const listWorkers = (filters?: WorkerFilters) => 
  workerService.list({ filters });

export const createWorker = (payload: WorkerCreate) => 
  workerService.create(payload);

export const updateWorker = (id: number, payload: WorkerUpdate) => 
  workerService.update(id, payload);

export const deleteWorker = (id: number) => 
  workerService.delete(id);

export const getWorkerTransportTypes = () => 
  workerService.getTransportTypes();

export const getAddressFixStats = () => 
  workerService.getAddressFixStats();

export const fixWorkerAddressesBatch = (batchSize?: number, startId?: number) => 
  workerService.fixAddressesBatch(batchSize, startId); 